// /src/store/modelStore.ts

import { create } from 'zustand';

// NOTE: Keep modelStore free of engine imports (no circular deps).
export type EditorMode = 'beginner' | 'advanced';
export type UiMode = 'edit' | 'instructions';
export type RenderMode = 'edit' | 'render';

export type Vec3 = { x: number; y: number; z: number };

export const MAIN_SUBMODEL_ID = 'main';

export type Submodel = {
  id: string;
  name: string;
};

export type StepState = {
  currentStep: number;
  viewStep: number;
};

// MPD-style: a submodel definition can be instantiated (referenced) inside another submodel.
// The instance stores the transform of the submodel file relative to its parent file.
export type SubmodelInstance = {
  id: string;
  submodelId: string;       // referenced submodel definition (child)
  parentSubmodelId: string; // container submodel definition (parent)
  matrix: number[];         // 16-length Matrix4 (THREE style), local to parent
  step: number;
};

export type PlacedPart = {
  id: string;
  partFile: string; // e.g. "3001.dat"
  name: string;
  ldrawColor: number;
  colorHex: string; // for UI / fallback materials
  matrix: number[]; // 16-length column-major Matrix4 (THREE style)
  step: number;
  submodelId: string;
};

export type PendingPlacement = {
  name: string;
  partFile: string;
  ldrawColor: number;
  colorHex: string;
  step: number;
  submodelId: string;
};

export type SnapSettings = {
  enabled: boolean;
  gridSnap: boolean;
  grid: number; // LDU
  heightSnap: boolean;
  height: number; // LDU
  rotSnap: boolean;
  rotDeg: number;
  connectorSnap: boolean;
  connectorThreshold: number; // LDU
  hingeSolver: boolean; // keep a matched connector locked while rotating
  alignOnConnect: boolean; // rotate-to-align when connector snapping
  multiConnector: boolean; // multi-connector agreement scoring
};

export type EditorSnapshot = {
  version: 4;
  parts: PlacedPart[];
  instances: SubmodelInstance[];
  submodels: Submodel[];
  activeSubmodelId: string | null;
  selectedId: string | null;
  currentStep: number;
  viewStep: number;
  stepStateBySubmodel?: Record<string, StepState>;
};

export type Toast = { id: string; title: string; detail?: string; kind?: 'info' | 'warn' | 'bad' | 'ok' };

type State = {
  mode: EditorMode;
  uiMode: UiMode;
  renderMode: RenderMode;
  snap: SnapSettings;
  parts: PlacedPart[];
  instances: SubmodelInstance[];
  submodels: Submodel[];
  activeSubmodelId: string | null;
  selectedId: string | null;
  pendingPlacement: PendingPlacement | null;
  currentStep: number;
  viewStep: number;
  stepStateBySubmodel: Record<string, StepState>;
  collision: 'ok' | 'warn' | 'bad';
  toasts: Toast[];

  // history
  undoStack: EditorSnapshot[];
  redoStack: EditorSnapshot[];

  // actions
  setMode: (m: EditorMode) => void;
  setUiMode: (m: UiMode) => void;
  setRenderMode: (m: RenderMode) => void;
  setSnap: (patch: Partial<SnapSettings>) => void;
  setActiveSubmodel: (id: string | null) => void;
  createSubmodel: (name: string) => string;
  updateSubmodel: (id: string, patch: Partial<Omit<Submodel, 'id'>>) => void;
  deleteSubmodel: (id: string) => void;
  setSelected: (id: string | null) => void;
  setPendingPlacement: (p: PendingPlacement | null) => void;
  addPart: (p: Omit<PlacedPart, 'id' | 'matrix' | 'submodelId'> & { matrix?: number[]; submodelId?: string }) => string;
  addInstance: (p: { submodelId: string; parentSubmodelId?: string; matrix?: number[]; step?: number }) => string;
  removeSelected: () => void;
  updatePartMatrix: (id: string, matrix: number[]) => void;
  updatePartProps: (id: string, patch: Partial<Omit<PlacedPart, 'id'>>) => void;
  updateInstanceMatrix: (id: string, matrix: number[]) => void;
  updateInstanceProps: (id: string, patch: Partial<Omit<SubmodelInstance, 'id'>>) => void;
  setCollision: (c: 'ok' | 'warn' | 'bad') => void;
  setCurrentStep: (s: number) => void;
  setViewStep: (s: number) => void;

  // file I/O
  replaceAll: (snapshot: EditorSnapshot, pushHistory?: boolean) => void;
  newModel: () => void;
  undo: () => void;
  redo: () => void;
  pushToast: (t: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
};

function normalizeSubmodels(list?: Submodel[]): Submodel[] {
  const base: Submodel[] = Array.isArray(list)
    ? list.map((sm) => ({ id: String(sm.id), name: String((sm as any).name ?? sm.id) }))
    : [];
  const hasMain = base.some((sm) => sm.id === MAIN_SUBMODEL_ID);
  if (!hasMain) base.unshift({ id: MAIN_SUBMODEL_ID, name: 'Main model' });

  const seen = new Set<string>();
  const out: Submodel[] = [];
  for (const sm of base) {
    if (seen.has(sm.id)) continue;
    seen.add(sm.id);
    out.push(sm);
  }
  return out;
}

function cloneSnapshot(s: EditorSnapshot): EditorSnapshot {
  const stepStateBySubmodel: Record<string, StepState> = Object.create(null);
  if (s.stepStateBySubmodel && typeof s.stepStateBySubmodel === 'object') {
    for (const [k, v] of Object.entries(s.stepStateBySubmodel)) {
      const cur = Math.max(1, Math.floor(Number((v as any).currentStep ?? 1)));
      const view = Math.max(1, Math.floor(Number((v as any).viewStep ?? 999)));
      stepStateBySubmodel[k] = { currentStep: cur, viewStep: view };
    }
  }
  return {
    version: 4,
    parts: s.parts.map((p) => ({ ...p, matrix: [...p.matrix] })),
    instances: (s.instances ?? []).map((i) => ({ ...i, matrix: [...i.matrix] })),
    submodels: normalizeSubmodels(s.submodels),
    activeSubmodelId: s.activeSubmodelId ?? null,
    selectedId: s.selectedId,
    currentStep: s.currentStep,
    viewStep: s.viewStep,
    stepStateBySubmodel,
  };
}

function snapshotFromState(get: () => State): EditorSnapshot {
  const st = get();
  return cloneSnapshot({
    version: 4,
    parts: st.parts.map((p) => ({ ...p, matrix: [...p.matrix] })),
    instances: st.instances.map((i) => ({ ...i, matrix: [...i.matrix] })),
    submodels: st.submodels.map((sm) => ({ ...sm })),
    activeSubmodelId: st.activeSubmodelId,
    selectedId: st.selectedId,
    currentStep: st.currentStep,
    viewStep: st.viewStep,
    stepStateBySubmodel: st.stepStateBySubmodel,
  });
}

function pushHistory(set: (fn: (s: State) => Partial<State>) => void, get: () => State) {
  const snap = snapshotFromState(get);
  set((s) => ({
    undoStack: [...s.undoStack, snap],
    redoStack: [],
  }));
}

const identityMatrix = () => [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
];

function uid(prefix = 'p') {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

function ctxId(activeSubmodelId: string | null) {
  return activeSubmodelId ?? MAIN_SUBMODEL_ID;
}

function maxStepForContext(parts: PlacedPart[], instances: SubmodelInstance[], submodelId: string) {
  let max = 1;
  for (const p of parts) {
    if ((p.submodelId ?? MAIN_SUBMODEL_ID) !== submodelId) continue;
    max = Math.max(max, Math.max(1, p.step ?? 1));
  }
  for (const i of instances) {
    if ((i.parentSubmodelId ?? MAIN_SUBMODEL_ID) !== submodelId) continue;
    max = Math.max(max, Math.max(1, i.step ?? 1));
  }
  return max;
}

function cloneStepStateMap(map: Record<string, StepState>) {
  const out: Record<string, StepState> = Object.create(null);
  for (const [k, v] of Object.entries(map ?? {})) {
    out[k] = {
      currentStep: Math.max(1, Math.floor(Number((v as any).currentStep ?? 1))),
      viewStep: Math.max(1, Math.floor(Number((v as any).viewStep ?? 999))),
    };
  }
  return out;
}

function wouldCreateCycle(
  instances: SubmodelInstance[],
  parentIdRaw: string,
  childIdRaw: string
): boolean {
  const parentId = String(parentIdRaw ?? MAIN_SUBMODEL_ID);
  const childId = String(childIdRaw ?? MAIN_SUBMODEL_ID);
  if (!parentId || !childId) return false;
  if (parentId === childId) return true;

  // Build adjacency: parentSubmodelId -> submodelId
  const adj = new Map<string, string[]>();
  for (const ins of instances ?? []) {
    const p = String(ins.parentSubmodelId ?? MAIN_SUBMODEL_ID);
    const c = String(ins.submodelId ?? MAIN_SUBMODEL_ID);
    if (!adj.has(p)) adj.set(p, []);
    adj.get(p)!.push(c);
  }

  // Proposed edge
  if (!adj.has(parentId)) adj.set(parentId, []);
  adj.get(parentId)!.push(childId);

  // Cycle if childId can reach parentId.
  const seen = new Set<string>();
  const stack = [childId];
  while (stack.length) {
    const n = stack.pop()!;
    if (n === parentId) return true;
    if (seen.has(n)) continue;
    seen.add(n);
    const next = adj.get(n) ?? [];
    for (const nxt of next) stack.push(nxt);
  }

  return false;
}

export const useModelStore = create<State>((set, get) => ({
  mode: 'beginner',
  uiMode: 'edit',
  renderMode: 'edit',
  snap: {
    enabled: true,
    gridSnap: true,
    grid: 20,
    heightSnap: true,
    height: 8,
    rotSnap: true,
    rotDeg: 90,
    connectorSnap: true,
    connectorThreshold: 12,
    hingeSolver: true,
    alignOnConnect: false,
    multiConnector: true,
  },
  parts: [],
  instances: [],
  submodels: [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }],
  activeSubmodelId: null,
  selectedId: null,
  pendingPlacement: null,
  currentStep: 1,
  viewStep: 999,
  stepStateBySubmodel: {
    [MAIN_SUBMODEL_ID]: { currentStep: 1, viewStep: 999 },
  },
  collision: 'ok',
  toasts: [],
  undoStack: [],
  redoStack: [],

  setMode: (m) => set({ mode: m }),
  setUiMode: (m) =>
    set((s) => ({
      uiMode: m,
      pendingPlacement: m === 'edit' ? s.pendingPlacement : null,
    })),
  setRenderMode: (m) => set({ renderMode: m }),
  setSnap: (patch) => set((s) => ({ snap: { ...s.snap, ...patch } })),
  setActiveSubmodel: (id) =>
    set((s) => {
      const prev = ctxId(s.activeSubmodelId);
      const next = ctxId(id);
      const map = cloneStepStateMap(s.stepStateBySubmodel);
      map[prev] = { currentStep: s.currentStep, viewStep: s.viewStep };
      if (!map[next]) {
        map[next] = {
          currentStep: maxStepForContext(s.parts, s.instances, next),
          viewStep: 999,
        };
      }
      const nextState = map[next];
      return {
        activeSubmodelId: id,
        stepStateBySubmodel: map,
        currentStep: nextState.currentStep,
        viewStep: nextState.viewStep,
      };
    }),

  createSubmodel: (name) => {
    pushHistory(set, get);
    const id = uid('sm');
    const nm = (name ?? '').trim() || 'Submodel';
    set((s) => {
      const prev = ctxId(s.activeSubmodelId);
      const map = cloneStepStateMap(s.stepStateBySubmodel);
      map[prev] = { currentStep: s.currentStep, viewStep: s.viewStep };
      map[id] = { currentStep: 1, viewStep: 999 };
      return {
        submodels: [...s.submodels, { id, name: nm }],
        activeSubmodelId: id,
        stepStateBySubmodel: map,
        currentStep: 1,
        viewStep: 999,
      };
    });
    return id;
  },

  updateSubmodel: (id, patch) => {
    pushHistory(set, get);
    set((s) => ({
      submodels: s.submodels.map((sm) => (sm.id === id ? { ...sm, ...patch } : sm)),
    }));
  },

  deleteSubmodel: (id) => {
    if (id === MAIN_SUBMODEL_ID) return;
  
    const st = get();
    if (!st.submodels.some((sm) => sm.id === id)) return;

    pushHistory(set, get);
  
    set((s) => {
      // Instances that directly reference the deleted submodel definition must be removed.
      const removedInstanceIds = new Set(
        s.instances.filter((ins) => ins.submodelId === id).map((ins) => ins.id)
      );

      const nextStepMap = cloneStepStateMap(s.stepStateBySubmodel);
      delete nextStepMap[id];
  
      const nextSubmodels = s.submodels.filter((sm) => sm.id !== id);
  
      // Parts that lived in the deleted submodel are moved to Main.
      const nextParts = s.parts.map((p) =>
        p.submodelId === id ? { ...p, submodelId: MAIN_SUBMODEL_ID } : p
      );
  
      // Instances:
      // 1) Remove instances that reference the deleted submodel def.
      // 2) Move instances that were *inside* the deleted submodel to Main.
      const nextInstances = s.instances
        .filter((ins) => ins.submodelId !== id)
        .map((ins) =>
          ins.parentSubmodelId === id
            ? { ...ins, parentSubmodelId: MAIN_SUBMODEL_ID }
            : ins
        );
  
      // Clear selection only if the selected item was removed.
      const nextSelected =
        s.selectedId && removedInstanceIds.has(s.selectedId) ? null : s.selectedId;
  
      const nextActive = s.activeSubmodelId === id ? null : s.activeSubmodelId;
      const nextCtx = ctxId(nextActive);
      const restored = nextStepMap[nextCtx] ?? { currentStep: 1, viewStep: 999 };
      if (!nextStepMap[nextCtx]) nextStepMap[nextCtx] = restored;

      return {
        submodels: nextSubmodels,
        parts: nextParts,
        instances: nextInstances,
        activeSubmodelId: nextActive,
        selectedId: nextSelected,
        stepStateBySubmodel: nextStepMap,
        currentStep: restored.currentStep,
        viewStep: restored.viewStep,
        collision: 'ok',
      };
    });
  },
  
  setSelected: (id) => set({ selectedId: id }),
  setPendingPlacement: (p) => set({ pendingPlacement: p ? { ...p } : null }),

  addPart: (p) => {
    pushHistory(set, get);
    const id = uid('part');
    const mat = p.matrix ?? identityMatrix();
    const submodelId = p.submodelId ?? get().activeSubmodelId ?? MAIN_SUBMODEL_ID;
    set((s) => ({
      parts: [...s.parts, { ...p, id, matrix: mat, submodelId }],
      selectedId: id,
      currentStep: ctxId(s.activeSubmodelId) === submodelId ? Math.max(s.currentStep, p.step) : s.currentStep,
      stepStateBySubmodel: {
        ...s.stepStateBySubmodel,
        [submodelId]: {
          currentStep: Math.max(s.stepStateBySubmodel[submodelId]?.currentStep ?? 1, p.step),
          viewStep: s.stepStateBySubmodel[submodelId]?.viewStep ?? 999,
        },
      },
    }));
    return id;
  },

  addInstance: (p) => {
    const st = get();
    const parentSubmodelId = String(p.parentSubmodelId ?? st.activeSubmodelId ?? MAIN_SUBMODEL_ID);
    const childId = String(p.submodelId);

    // Block cycles at creation time (better UX than renderer stack overflow).
    if (wouldCreateCycle(st.instances, parentSubmodelId, childId)) {
      st.pushToast({
        title: 'Cycle blocked',
        detail: 'That instance would create a submodel loop.',
        kind: 'bad',
      });
      return '';
    }
    
    pushHistory(set, get);
    const id = uid('inst');
    const mat = p.matrix ?? identityMatrix();
    const step = Math.max(1, Math.floor(p.step ?? st.currentStep ?? 1));
    set((s) => ({
      instances: [
        ...s.instances,
        {
          id,
          submodelId: childId,
          parentSubmodelId: String(parentSubmodelId),
          matrix: mat,
          step,
        },
      ],
      selectedId: id,
      currentStep: ctxId(s.activeSubmodelId) === parentSubmodelId ? Math.max(s.currentStep, step) : s.currentStep,
      stepStateBySubmodel: {
        ...s.stepStateBySubmodel,
        [parentSubmodelId]: {
          currentStep: Math.max(s.stepStateBySubmodel[parentSubmodelId]?.currentStep ?? 1, step),
          viewStep: s.stepStateBySubmodel[parentSubmodelId]?.viewStep ?? 999,
        },
      },
    }));
    return id;
  },  

  removeSelected: () => {
    const id = get().selectedId;
    if (!id) return;
    pushHistory(set, get);
    set((s) => ({
      parts: s.parts.filter((p) => p.id !== id),
      instances: s.instances.filter((ins) => ins.id !== id),
      selectedId: null,
    }));
  },

  updatePartMatrix: (id, matrix) => {
    set((s) => ({
      parts: s.parts.map((p) => (p.id === id ? { ...p, matrix: [...matrix] } : p)),
    }));
  },

  updatePartProps: (id, patch) => {
    pushHistory(set, get);
    set((s) => ({
      parts: s.parts.map((p) => (p.id === id ? { ...p, ...patch, matrix: patch.matrix ? [...patch.matrix] : p.matrix } : p)),
    }));
  },

  updateInstanceMatrix: (id, matrix) => {
    set((s) => ({
      instances: s.instances.map((ins) => (ins.id === id ? { ...ins, matrix: [...matrix] } : ins)),
    }));
  },

  updateInstanceProps: (id, patch) => {
    const st = get();
    const cur = st.instances.find((ins) => ins.id === id);
    if (!cur) return;

    const nextParent = String((patch as any).parentSubmodelId ?? cur.parentSubmodelId ?? MAIN_SUBMODEL_ID);
    const nextChild = String((patch as any).submodelId ?? cur.submodelId ?? MAIN_SUBMODEL_ID);

    const changingEdge =
      (typeof (patch as any).parentSubmodelId === 'string' && nextParent !== cur.parentSubmodelId) ||
      (typeof (patch as any).submodelId === 'string' && nextChild !== cur.submodelId);

    if (changingEdge) {
      // Remove current edge first, then test new edge (avoids false positives when "swapping").
      const without = st.instances.filter((ins) => ins.id !== id);
      if (wouldCreateCycle(without, nextParent, nextChild)) {
        st.pushToast({
          title: 'Cycle blocked',
          detail: 'That change would create a submodel loop.',
          kind: 'bad',
        });
        return;
      }
    }
  
    pushHistory(set, get);
    set((s) => ({
      instances: s.instances.map((ins) =>
        ins.id === id
          ? {
              ...ins,
              ...patch,
              submodelId: nextChild,
              parentSubmodelId: nextParent,
              matrix: (patch as any).matrix ? [...(patch as any).matrix] : ins.matrix,
            }
          : ins
      ),
    }));
  },  

  setCollision: (c) => set({ collision: c }),
  setCurrentStep: (step) =>
    set((s) => {
      const v = Math.max(1, Math.floor(step));
      const c = ctxId(s.activeSubmodelId);
      return {
        currentStep: v,
        stepStateBySubmodel: {
          ...s.stepStateBySubmodel,
          [c]: { currentStep: v, viewStep: s.viewStep },
        },
      };
    }),
  setViewStep: (step) =>
    set((s) => {
      const v = Math.max(1, Math.floor(step));
      const c = ctxId(s.activeSubmodelId);
      return {
        viewStep: v,
        stepStateBySubmodel: {
          ...s.stepStateBySubmodel,
          [c]: { currentStep: s.currentStep, viewStep: v },
        },
      };
    }),

  replaceAll: (snapshot, push = true) => {
    if (push) pushHistory(set, get);
    const submodels = normalizeSubmodels(snapshot.submodels);
    const active = snapshot.activeSubmodelId && submodels.some((sm) => sm.id === snapshot.activeSubmodelId)
      ? snapshot.activeSubmodelId
      : null;
    const ctx = ctxId(active);
    const baseMap: Record<string, StepState> = Object.create(null);
    for (const sm of submodels) {
      baseMap[sm.id] = { currentStep: maxStepForContext(snapshot.parts, snapshot.instances ?? [], sm.id), viewStep: 999 };
    }
    const merged = { ...baseMap, ...(snapshot.stepStateBySubmodel ?? {}) };
    const stepMap = cloneStepStateMap(merged as any);
    // Back-compat: if no per-submodel map was present, use snapshot's current/view for the active context.
    if (!snapshot.stepStateBySubmodel) {
      stepMap[ctx] = { currentStep: Math.max(1, snapshot.currentStep ?? 1), viewStep: Math.max(1, snapshot.viewStep ?? 999) };
    }
    const restored = stepMap[ctx] ?? { currentStep: 1, viewStep: 999 };
    set({
      parts: snapshot.parts.map((p) => ({ ...p, matrix: [...p.matrix], submodelId: p.submodelId ?? MAIN_SUBMODEL_ID })),
      instances: (snapshot.instances ?? []).map((i) => ({
        ...i,
        matrix: [...i.matrix],
        parentSubmodelId: i.parentSubmodelId ?? MAIN_SUBMODEL_ID,
      })),
      submodels,
      activeSubmodelId: active,
      selectedId: snapshot.selectedId,
      pendingPlacement: null,
      stepStateBySubmodel: stepMap,
      currentStep: restored.currentStep,
      viewStep: restored.viewStep,
      collision: 'ok',
    });
  },

  newModel: () => {
    pushHistory(set, get);
    set({
      parts: [],
      instances: [],
      submodels: [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }],
      activeSubmodelId: null,
      selectedId: null,
      pendingPlacement: null,
      currentStep: 1,
      viewStep: 999,
      stepStateBySubmodel: { [MAIN_SUBMODEL_ID]: { currentStep: 1, viewStep: 999 } },
      collision: 'ok',
    });
  },

  undo: () => {
    const s = get();
    const prev = s.undoStack.at(-1);
    if (!prev) return;
  
    const cur = snapshotFromState(get);

    set({
      undoStack: s.undoStack.slice(0, -1),
      redoStack: [...s.redoStack, cur],
    });

    // Canonical restore path (also fixes step-map desync bugs)
    get().replaceAll(prev, false);
  },
  
  redo: () => {
    const s = get();
    const next = s.redoStack.at(-1);
    if (!next) return;
  
    const cur = snapshotFromState(get);

    set({
      redoStack: s.redoStack.slice(0, -1),
      undoStack: [...s.undoStack, cur],
    });

    get().replaceAll(next, false);
  },

  pushToast: (t) => {
    const id = uid('toast');
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    // auto-dismiss
    setTimeout(() => {
      get().dismissToast(id);
    }, 3500);
  },
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const selectSelectedPart = (s: Pick<State, 'parts' | 'selectedId'>) =>
  s.parts.find((p) => p.id === s.selectedId) ?? null;

export const selectSelectedInstance = (s: Pick<State, 'instances' | 'selectedId'>) =>
  s.instances.find((i) => i.id === s.selectedId) ?? null;

export const toSnapshot = (s: Pick<State, 'parts' | 'instances' | 'submodels' | 'activeSubmodelId' | 'selectedId' | 'currentStep' | 'viewStep'>): EditorSnapshot =>
  cloneSnapshot({
    version: 4,
    parts: s.parts,
    instances: s.instances,
    submodels: s.submodels,
    activeSubmodelId: s.activeSubmodelId,
    selectedId: s.selectedId,
    currentStep: s.currentStep,
    viewStep: s.viewStep,
    stepStateBySubmodel: (s as any).stepStateBySubmodel,
  });
